/**
 * Bookings screen — the aggregated reservations register (§6 M2).
 *
 * A single place to see EVERY booking across direct and OTA channels (they all
 * live in one `reservation` table). Fully dynamic via the real
 * `/api/booking/bookings/list` endpoint:
 *   - a summary strip (total, upcoming, confirmed, held, cancelled, revenue);
 *   - a filter bar: text search (guest / confirmation code), status, channel,
 *     payment status, and a check-in date range;
 *   - a table: guest, property/unit, party size, dates + nights, channel,
 *     amount, payment status, status;
 *   - a row-click detail drawer with the full booking.
 *
 * The single gold element (§07) is the confirmed-revenue summary figure.
 *
 * SSR-safe: initial render is a pure loading state; all fetches + Date reads
 * run inside effects/handlers.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  useRef,
} from "react";
import {
  apiListBookings,
  apiListProperties,
  apiExpireHolds,
  apiCancelBooking,
  apiModifyBooking,
  apiBookingHistory,
  apiRateCalendar,
  apiQuote,
  asError,
  formatMoney,
  type BookingRow,
  type BookingsSummary,
  type BookingsFilter,
  type PaymentStatus,
  type Property,
  type ReservationStatus,
  type BookingHistoryEvent,
} from "../services.js";
import { MoneyValue } from "../components/MoneyValue.js";
import { BookingsCalendar, type CalendarDay } from "../BookingsCalendar.js";
import { OtaLogo } from "../OtaLogo.js";

/** How many rows per page in the table view. */
const PAGE_SIZE = 20;

/** Length of the calendar timeline window, in days. */
const CALENDAR_DAYS = 14;

/** Single-letter weekday initials, Sunday-first. */
const WEEKDAY_INITIALS = ["S", "M", "T", "W", "T", "F", "S"] as const;

/** Add `n` days to an ISO `YYYY-MM-DD`, returning a new ISO date (UTC-safe). */
function addDaysIso(iso: string, n: number): string {
  const ms = Date.parse(`${iso}T00:00:00Z`) + n * 86_400_000;
  return new Date(ms).toISOString().slice(0, 10);
}

/** Today's local date as ISO `YYYY-MM-DD`. */
function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Build the calendar day descriptors for a window starting at `startIso`. */
function buildDays(startIso: string, len: number): CalendarDay[] {
  const today = todayIso();
  const out: CalendarDay[] = [];
  for (let i = 0; i < len; i += 1) {
    const iso = addDaysIso(startIso, i);
    const d = new Date(`${iso}T00:00:00Z`);
    const dow = d.getUTCDay();
    out.push({
      iso,
      dayNum: d.getUTCDate(),
      weekday: WEEKDAY_INITIALS[dow] as string,
      isToday: iso === today,
      isWeekend: dow === 0 || dow === 6,
    });
  }
  return out;
}

/** Human labels for reservation status. */
const STATUS_LABEL: Record<ReservationStatus, string> = {
  held: "Held",
  confirmed: "Confirmed",
  cancelled: "Cancelled",
  overbooked: "Overbooked",
};

/** Human labels for the derived payment status. */
const PAYMENT_LABEL: Record<PaymentStatus, string> = {
  paid: "Paid",
  deposit_paid: "Deposit paid",
  pay_at_property: "Pay at property",
  pending: "Pending",
  refunded: "Refunded",
  cancelled: "Cancelled",
};

/** Map a reservation status to a shared status-pill modifier. */
function statusPill(status: ReservationStatus): string {
  switch (status) {
    case "confirmed":
      return "avos-status avos-status--ok";
    case "held":
      return "avos-status avos-status--pending";
    case "overbooked":
      return "avos-status avos-status--attention";
    case "cancelled":
    default:
      return "avos-status avos-status--critical";
  }
}

/** Map a payment status to a shared status-pill modifier. */
function paymentPill(p: PaymentStatus): string {
  switch (p) {
    case "paid":
      return "avos-status avos-status--ok";
    case "deposit_paid":
    case "pay_at_property":
      return "avos-status avos-status--attention";
    case "pending":
      return "avos-status avos-status--pending";
    case "refunded":
    case "cancelled":
    default:
      return "avos-status avos-status--critical";
  }
}

/** A displayable guest label, falling back to a short id. */
function guestLabel(name: string | null, id: string): string {
  return name !== null && name.trim() !== "" ? name : `Guest ${id.slice(0, 8)}`;
}

/** Format an ISO date `YYYY-MM-DD` as a compact readable date. */
function fmtDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

/**
 * Format an ISO timestamp compactly for a "last sync" style display, or the em
 * dash when there is genuinely no value. Guarded against invalid input; used
 * only inside render post-mount (initial render is the pure loading state).
 */
function fmtDateTime(iso: string | null | undefined): string {
  if (iso === null || iso === undefined || iso === "") return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

/** Human display names for a booking source code. Never surfaces "Channex". */
const SOURCE_LABEL: Record<string, string> = {
  direct: "Direct",
  walk_in: "Walk-in",
  manual: "Manual",
  airbnb: "Airbnb",
  booking_com: "Booking.com",
  makemytrip: "MakeMyTrip",
  goibibo: "Goibibo",
  agoda: "Agoda",
  expedia: "Expedia",
  trip_com: "Trip.com",
  other_ota: "OTA",
};

/**
 * Map a source code to its display name. Falls back to the raw `channel`
 * string when the source is undefined/empty. Pure.
 */
function sourceLabel(source: string | undefined, channel: string): string {
  if (source === undefined || source === "") return channel;
  return SOURCE_LABEL[source] ?? channel;
}

/**
 * A booking's display source: "Direct · <acquisition>" for a direct/manual/
 * walk-in reservation whose `channel` carries the acquisition label (Website /
 * WhatsApp / Phone / …), else the OTA source name. Never surfaces "Channex".
 */
function displaySource(b: BookingRow): string {
  const isDirect = b.sourceType === "direct" || b.source === "direct";
  const isWalkIn = b.sourceType === "walk_in" || b.source === "walk_in";
  const isManual = b.sourceType === "manual" || b.source === "manual";
  if (isDirect || isWalkIn || isManual) {
    const base = isWalkIn ? "Walk-in" : isManual ? "Manual" : "Direct";
    const acq = (b.channel ?? "").trim();
    // Show the acquisition label when it adds information (e.g. WhatsApp).
    if (acq !== "" && acq.toLowerCase() !== base.toLowerCase() && acq.toLowerCase() !== "direct") {
      return `${base} · ${acq}`;
    }
    return base;
  }
  return sourceLabel(b.source, b.channel);
}

/** The Source filter catalogue (real supported sources). Value = match key. */
const SOURCE_FILTERS: readonly { readonly value: string; readonly label: string }[] = [
  { value: "direct", label: "Direct" },
  { value: "booking_com", label: "Booking.com" },
  { value: "airbnb", label: "Airbnb" },
  { value: "trip_com", label: "Trip.com" },
  { value: "makemytrip", label: "MakeMyTrip / Goibibo" },
  { value: "agoda", label: "Agoda" },
  { value: "expedia", label: "Expedia" },
  { value: "walk_in", label: "Walk-in" },
  { value: "manual", label: "Manual" },
];

/** Whether a booking row matches a Source filter value. Pure. */
function matchesSource(b: BookingRow, filter: string): boolean {
  if (filter === "") return true;
  if (filter === "direct") {
    // "Direct" groups every direct-origin reservation (Website/WhatsApp/…)
    return b.sourceType === "direct" || b.source === "direct";
  }
  if (filter === "makemytrip") return b.source === "makemytrip" || b.source === "goibibo";
  return b.source === filter;
}

/** Map a channel sync status to a pill class + label. Pure. */
function syncPill(status: string | undefined): { cls: string; label: string } {
  switch (status) {
    case "synced":
      return { cls: "avos-status avos-status--ok", label: "Synced" };
    case "pending":
      return { cls: "avos-status avos-status--pending", label: "Pending" };
    case "conflict":
      return { cls: "avos-status avos-status--attention", label: "Conflict" };
    case "mapping_required":
      return { cls: "avos-status avos-status--attention", label: "Mapping required" };
    case "failed":
      return { cls: "avos-status avos-status--critical", label: "Failed" };
    case "not_applicable":
    default:
      return { cls: "avos-status", label: "—" };
  }
}

// ===========================================================================
// Summary strip
// ===========================================================================

function SummaryStrip({ summary }: { readonly summary: BookingsSummary }): ReactElement {
  return (
    <div className="console-card-grid bookings-summary">
      <div className="console-card">
        <p className="console-card__label">Total bookings</p>
        <p className="console-card__value">{summary.total}</p>
      </div>
      <div className="console-card">
        <p className="console-card__label">Upcoming</p>
        <p className="console-card__value">{summary.upcoming}</p>
        <p className="console-card__hint">confirmed, today or later</p>
      </div>
      <div className="console-card">
        <p className="console-card__label">Held</p>
        <p className="console-card__value">{summary.held}</p>
      </div>
      <div className="console-card console-card--gold">
        <p className="console-card__label">Confirmed revenue</p>
        <MoneyValue
          minor={Number(summary.confirmedRevenueMinor)}
          currency={summary.currency}
        />
        <p className="console-card__hint">{summary.confirmed} confirmed</p>
      </div>
    </div>
  );
}

// ===========================================================================
// Detail drawer
// ===========================================================================

/** True when a booking is AVOS-created (editable/cancellable in AVOS). */
function isDirectBooking(b: BookingRow): boolean {
  return b.sourceType === "direct" || b.sourceType === "manual" || b.sourceType === "walk_in"
    || b.source === "direct" || b.source === "manual" || b.source === "walk_in";
}

function DetailDrawer({
  booking,
  onClose,
  onChanged,
}: {
  readonly booking: BookingRow;
  readonly onClose: () => void;
  readonly onChanged: (flash: string) => void;
}): ReactElement {
  const [history, setHistory] = useState<readonly BookingHistoryEvent[]>([]);
  const [editing, setEditing] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    let active = true;
    void apiBookingHistory(booking.id).then((ev) => { if (active) setHistory(ev); });
    return () => { active = false; };
  }, [booking.id]);

  const direct = isDirectBooking(booking);
  const isCancelled = booking.status === "cancelled";
  const canEdit = direct && !isCancelled;
  const canCancel = direct && !isCancelled && (booking.status === "held" || booking.status === "confirmed");

  const row = (label: string, value: string): ReactElement => (
    <div className="bookings-drawer__row">
      <span className="bookings-drawer__label">{label}</span>
      <span className="bookings-drawer__value">{value}</span>
    </div>
  );

  return (
    <div
      className="bookings-drawer-scrim"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <aside
        className="bookings-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Booking details"
      >
        <header className="bookings-drawer__header">
          <div>
            <p className="bookings-drawer__eyebrow">Booking</p>
            <h2 className="bookings-drawer__title">
              {guestLabel(booking.guestName, booking.guestId)}
            </h2>
          </div>
          <button
            type="button"
            className="console-iconbtn bookings-drawer__close"
            aria-label="Close"
            onClick={onClose}
          >
            <span aria-hidden="true">✕</span>
          </button>
        </header>
        <div className="bookings-drawer__body">
          <div className="bookings-drawer__pills">
            <span className={statusPill(booking.status)}>
              {STATUS_LABEL[booking.status]}
            </span>
            <span className={paymentPill(booking.paymentStatus)}>
              {PAYMENT_LABEL[booking.paymentStatus]}
            </span>
            <span className="avos-status">
              {displaySource(booking)}
            </span>
          </div>
          {booking.confirmationCode !== null
            ? row("Confirmation", booking.confirmationCode)
            : null}
          {booking.guestEmail !== null ? row("Email", booking.guestEmail) : null}
          {row("Property", booking.propertyName ?? "—")}
          {row("Unit", booking.unitName ?? "—")}
          {row("Guests", booking.partySize !== null ? String(booking.partySize) : "—")}
          {row("Check-in", fmtDate(booking.checkIn))}
          {row("Check-out", fmtDate(booking.checkOut))}
          {row("Nights", String(booking.nights))}
          {row("Amount", formatMoney(Number(booking.amountMinor), booking.currency))}
          {row("Deposit", formatMoney(Number(booking.depositMinor), booking.currency))}
          {booking.refundMinor !== null
            ? row("Refund", formatMoney(Number(booking.refundMinor), booking.currency))
            : null}

          {/* ACTIONS — Edit / Cancel for AVOS-created bookings only. */}
          <div className="bookings-drawer__actions" style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem", flexWrap: "wrap" }}>
            {canEdit ? (
              <button type="button" className="console-btn" onClick={() => setEditing(true)}>Edit booking</button>
            ) : null}
            {canCancel ? (
              <button type="button" className="console-btn--ghost" onClick={() => setCancelling(true)}>Cancel booking</button>
            ) : null}
          </div>
          {!direct ? (
            <p className="bookings-drawer__ota-note rev-muted" role="note" style={{ marginTop: "0.5rem" }}>
              This reservation is managed by {sourceLabel(booking.source, booking.channel)}. Modify or cancel it through the source channel. AVOS updates automatically after the channel sends the change.
            </p>
          ) : null}
          {isCancelled ? (
            <div className="bookings-drawer__cancelled" style={{ marginTop: "0.5rem" }}>
              <span className="avos-status avos-status--critical">Cancelled</span>
              {booking.cancellationReason ? row("Reason", booking.cancellationReason) : null}
            </div>
          ) : null}

          {booking.sourceType === "ota" ? (
            <>
              <h3 className="console-form__title bookings-drawer__subtitle">
                Channel information
              </h3>
              {row("Source", sourceLabel(booking.source, booking.channel))}
              {row("OTA reference", booking.otaReservationCode ?? "—")}
              {row("Channel provider", "Channex")}
              {row("Sync status", syncPill(booking.channelSyncStatus).label)}
              {row("Last sync", fmtDateTime(booking.channelLastSyncedAt))}
              {booking.adults !== null &&
              booking.adults !== undefined &&
              booking.children !== null &&
              booking.children !== undefined
                ? row(
                    "Occupancy",
                    `${booking.adults ?? 0} adults, ${booking.children ?? 0} children${
                      booking.infants !== null &&
                      booking.infants !== undefined &&
                      booking.infants > 0
                        ? `, ${booking.infants} infants`
                        : ""
                    }`,
                  )
                : null}
              <details className="bookings-drawer__tech">
                <summary>View technical details</summary>
                {booking.channexBookingId !== null &&
                booking.channexBookingId !== undefined
                  ? row("Channex booking id", booking.channexBookingId)
                  : null}
                {booking.channexRevisionId !== null &&
                booking.channexRevisionId !== undefined
                  ? row("Channex revision id", booking.channexRevisionId)
                  : null}
                {booking.externalUniqueId !== null &&
                booking.externalUniqueId !== undefined
                  ? row("External unique id", booking.externalUniqueId)
                  : null}
                {booking.providerCode !== null &&
                booking.providerCode !== undefined
                  ? row("Provider code", booking.providerCode)
                  : null}
              </details>
            </>
          ) : null}

          {/* BOOKING HISTORY timeline (lightweight audit). */}
          <h3 className="console-form__title bookings-drawer__subtitle">Booking history</h3>
          {history.length === 0 ? (
            <p className="rev-muted rev-small">No history recorded yet.</p>
          ) : (
            <ul className="bookings-drawer__history" style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {history.map((e) => (
                <li key={e.id} style={{ padding: "0.35rem 0", borderBottom: "1px solid var(--console-border, #eee)" }}>
                  <div className="rev-small rev-muted">{new Date(e.createdAt).toLocaleString()}{e.actorName ? ` · ${e.actorName}` : ""}</div>
                  <div>{e.summary}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>

      {editing ? (
        <EditBookingModal
          booking={booking}
          onClose={() => setEditing(false)}
          onSaved={(flash) => { setEditing(false); onChanged(flash); }}
        />
      ) : null}
      {cancelling ? (
        <CancelBookingModal
          booking={booking}
          onClose={() => setCancelling(false)}
          onCancelled={(flash) => { setCancelling(false); onChanged(flash); }}
        />
      ) : null}
    </div>
  );
}

/** Suggested cancellation reasons (certification §15). */
const CANCEL_REASONS = [
  "Guest requested",
  "Duplicate booking",
  "Payment issue",
  "Property unavailable",
  "Owner request",
  "Other",
] as const;

function CancelBookingModal({
  booking,
  onClose,
  onCancelled,
}: {
  readonly booking: BookingRow;
  readonly onClose: () => void;
  readonly onCancelled: (flash: string) => void;
}): ReactElement {
  const [reason, setReason] = useState<string>(CANCEL_REASONS[0]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(): Promise<void> {
    if (busy) return; // double-click protection
    setBusy(true); setError(null);
    try {
      const res = await apiCancelBooking({
        reservationId: booking.id,
        reason,
        ...(note.trim() !== "" ? { note: note.trim() } : {}),
      });
      const e = asError(res);
      if (e) { setError(e.error.message); setBusy(false); return; }
      onCancelled(`Booking ${booking.confirmationCode ?? booking.id} cancelled.`);
    } catch { setError("Could not cancel the booking."); setBusy(false); }
  }

  return (
    <div className="rev-modal" role="dialog" aria-modal="true" aria-label="Cancel booking">
      <div className="rev-modal__backdrop" onClick={busy ? undefined : onClose} />
      <div className="rev-modal__panel">
        <h3 className="rev-modal__title">Cancel booking</h3>
        <p className="rev-muted rev-small">This keeps the reservation on record and releases its inventory.</p>
        <div className="console-field">
          <label htmlFor="cancel-reason">Cancellation reason</label>
          <select id="cancel-reason" value={reason} onChange={(e) => setReason(e.target.value)}>
            {CANCEL_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div className="console-field">
          <label htmlFor="cancel-note">Internal note (optional)</label>
          <textarea id="cancel-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} />
        </div>
        {error ? <p className="rev-error" role="alert">{error}</p> : null}
        <div className="rev-modal__actions">
          <button type="button" className="console-btn--ghost" onClick={onClose} disabled={busy}>Keep booking</button>
          <button type="button" className="console-btn" onClick={() => void submit()} disabled={busy}>
            {busy ? "Cancelling…" : "Cancel booking"}
          </button>
        </div>
      </div>
    </div>
  );
}

interface EditRoomOption { readonly unitId: string; readonly unitName: string; readonly ratePlans: { id: string; name: string }[] }

function EditBookingModal({
  booking,
  onClose,
  onSaved,
}: {
  readonly booking: BookingRow;
  readonly onClose: () => void;
  readonly onSaved: (flash: string) => void;
}): ReactElement {
  const [checkIn, setCheckIn] = useState(booking.checkIn);
  const [checkOut, setCheckOut] = useState(booking.checkOut);
  const [unitId, setUnitId] = useState(booking.unitId);
  const [ratePlanId, setRatePlanId] = useState<string>("");
  const [rooms, setRooms] = useState<readonly EditRoomOption[]>([]);
  const [quote, setQuote] = useState<{ available: boolean | null; totalMinor: string; nightlyRateMinor: string; currency: string } | null>(null);
  const [quoting, setQuoting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load the property's rooms + their rate plans for the room/rate selectors.
  useEffect(() => {
    let active = true;
    void apiRateCalendar({ propertyId: booking.propertyId, from: checkIn, to: checkIn }).then((res) => {
      if (!active) return;
      if (asError(res)) return;
      const r = res as unknown as { rows: ReadonlyArray<{ unitId: string; unitName: string; ratePlans?: ReadonlyArray<{ ratePlanId: string; ratePlanName: string }> }> };
      setRooms(r.rows.map((row) => ({
        unitId: row.unitId, unitName: row.unitName,
        ratePlans: (row.ratePlans ?? []).map((rp) => ({ id: rp.ratePlanId, name: rp.ratePlanName })),
      })));
    });
    return () => { active = false; };
  }, [booking.propertyId, checkIn]);

  // Default the rate plan to the selected room's first plan.
  useEffect(() => {
    const room = rooms.find((r) => r.unitId === unitId);
    if (room && room.ratePlans.length > 0 && !room.ratePlans.some((rp) => rp.id === ratePlanId)) {
      setRatePlanId(room.ratePlans[0]!.id);
    }
  }, [rooms, unitId, ratePlanId]);

  // Live availability + price preview whenever the stay/room/rate changes.
  useEffect(() => {
    let active = true;
    if (checkOut <= checkIn) { setQuote(null); return; }
    setQuoting(true);
    void apiQuote({ unitId, checkIn, checkOut, ...(ratePlanId ? { ratePlanId } : {}) } as never).then((res) => {
      if (!active) return;
      setQuoting(false);
      if (asError(res)) { setQuote(null); return; }
      const q = res as { available: boolean | null; totalMinor: string; nightlyRateMinor: string; currency: string };
      setQuote({ available: q.available, totalMinor: q.totalMinor, nightlyRateMinor: q.nightlyRateMinor, currency: q.currency });
    });
    return () => { active = false; };
  }, [unitId, checkIn, checkOut, ratePlanId]);

  const room = rooms.find((r) => r.unitId === unitId);
  const changed = checkIn !== booking.checkIn || checkOut !== booking.checkOut || unitId !== booking.unitId;

  async function save(): Promise<void> {
    if (busy) return; // double-click protection
    if (checkOut <= checkIn) { setError("Check-out must be after check-in."); return; }
    setBusy(true); setError(null);
    try {
      const res = await apiModifyBooking({
        reservationId: booking.id,
        ...(unitId !== booking.unitId ? { unitId } : {}),
        ...(checkIn !== booking.checkIn ? { checkIn } : {}),
        ...(checkOut !== booking.checkOut ? { checkOut } : {}),
        ...(quote && changed ? { nightlyRateMinor: Number(quote.nightlyRateMinor) } : {}),
      });
      const e = asError(res);
      if (e) { setError(e.error.message); setBusy(false); return; }
      onSaved(`Booking ${booking.confirmationCode ?? booking.id} updated successfully.`);
    } catch { setError("Could not update the booking."); setBusy(false); }
  }

  const unavailable = quote !== null && quote.available === false;

  return (
    <div className="rev-modal" role="dialog" aria-modal="true" aria-label="Edit booking">
      <div className="rev-modal__backdrop" onClick={busy ? undefined : onClose} />
      <div className="rev-modal__panel">
        <h3 className="rev-modal__title">Edit booking · {booking.confirmationCode ?? ""}</h3>
        <div className="console-form__row">
          <div className="console-field">
            <label htmlFor="edit-room">Room</label>
            <select id="edit-room" value={unitId} onChange={(e) => setUnitId(e.target.value)}>
              {rooms.length === 0 ? <option value={booking.unitId}>{booking.unitName ?? "Room"}</option> : null}
              {rooms.map((r) => <option key={r.unitId} value={r.unitId}>{r.unitName}</option>)}
            </select>
          </div>
          <div className="console-field">
            <label htmlFor="edit-rate">Rate plan</label>
            <select id="edit-rate" value={ratePlanId} onChange={(e) => setRatePlanId(e.target.value)}>
              {(room?.ratePlans ?? []).map((rp) => <option key={rp.id} value={rp.id}>{rp.name}</option>)}
            </select>
          </div>
        </div>
        <div className="console-form__row">
          <div className="console-field">
            <label htmlFor="edit-ci">Check-in</label>
            <input id="edit-ci" type="date" value={checkIn} onChange={(e) => setCheckIn(e.target.value)} />
          </div>
          <div className="console-field">
            <label htmlFor="edit-co">Check-out</label>
            <input id="edit-co" type="date" value={checkOut} onChange={(e) => setCheckOut(e.target.value)} />
          </div>
        </div>

        {/* Compact before/after summary (certification §12). */}
        <div className="bookings-edit__summary rev-small" style={{ marginTop: "0.5rem" }}>
          <div><strong>Original:</strong> {booking.unitName ?? "Room"} · {fmtDate(booking.checkIn)} → {fmtDate(booking.checkOut)} · {formatMoney(Number(booking.amountMinor), booking.currency)}</div>
          <div>
            <strong>Updated:</strong> {room?.unitName ?? booking.unitName ?? "Room"} · {fmtDate(checkIn)} → {fmtDate(checkOut)}
            {quote ? ` · ${formatMoney(Number(quote.totalMinor), quote.currency)}` : (quoting ? " · …" : "")}
          </div>
          {changed ? <div className="rev-muted">Inventory impact: old dates released · new dates reserved.</div> : null}
        </div>

        {unavailable ? <p className="rev-error" role="alert">{room?.unitName ?? "This room"} is not available for {fmtDate(checkIn)} – {fmtDate(checkOut)}.</p> : null}
        {error ? <p className="rev-error" role="alert">{error}</p> : null}
        <div className="rev-modal__actions">
          <button type="button" className="console-btn--ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="console-btn" onClick={() => void save()} disabled={busy || quoting || unavailable || checkOut <= checkIn}>
            {busy ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// Top-level screen
// ===========================================================================

type LoadState =
  | { readonly kind: "loading" }
  | { readonly kind: "ready"; readonly rows: readonly BookingRow[]; readonly summary: BookingsSummary }
  | { readonly kind: "error" };

const PAYMENT_STATUSES: readonly PaymentStatus[] = [
  "paid",
  "deposit_paid",
  "pay_at_property",
  "pending",
  "refunded",
  "cancelled",
];

export default function BookingsScreen(): ReactElement {
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [properties, setProperties] = useState<readonly Property[]>([]);
  const [selected, setSelected] = useState<BookingRow | null>(null);
  const [expiring, setExpiring] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);

  // View + pagination + calendar window.
  const [view, setView] = useState<"table" | "calendar">("table");
  const [page, setPage] = useState(1);
  const [calStart, setCalStart] = useState<string>(() => todayIso());

  // Filter state. Property + Source are applied client-side (so KPIs, table and
  // calendar all stay consistent under the same filter context).
  const [search, setSearch] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [source, setSource] = useState("");
  const [status, setStatus] = useState<"" | ReservationStatus>("");
  const [paymentStatus, setPaymentStatus] = useState<"" | PaymentStatus>("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = useCallback(async (): Promise<void> => {
    setState({ kind: "loading" });
    try {
      const filter: BookingsFilter = {
        ...(status !== "" ? { status } : {}),
        ...(search.trim() !== "" ? { search: search.trim() } : {}),
        ...(from !== "" ? { from } : {}),
        ...(to !== "" ? { to } : {}),
      };
      const result = await apiListBookings(filter);
      setState({ kind: "ready", rows: result.rows, summary: result.summary });
    } catch {
      setState({ kind: "error" });
    }
  }, [status, search, from, to]);

  // Initial load: the property list for the Property filter.
  useEffect(() => {
    let active = true;
    apiListProperties()
      .then((ps) => {
        if (active) setProperties(ps);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // DEEP LINK (from a booking notification): when the page is opened with
  // ?booking=<reservationId>, auto-select that reservation so the EXISTING
  // detail drawer opens — no manual search/row-click. Runs once per id after
  // the rows are loaded; if the id is not found we simply load the list
  // normally (no crash). The param is cleared so a refresh/back doesn't
  // re-trigger. Reuses the current drawer (setSelected) — no second UI.
  const deepLinkHandled = useRef<string | null>(null);
  useEffect(() => {
    if (state.kind !== "ready" || typeof window === "undefined") return;
    const id = new URLSearchParams(window.location.search).get("booking");
    if (id === null || id === "" || deepLinkHandled.current === id) return;
    deepLinkHandled.current = id;
    const match = state.rows.find((r) => r.id === id);
    if (match !== undefined) {
      setSelected(match);
    }
    // Clear the query param either way (found or not) so it is one-shot.
    const url = new URL(window.location.href);
    url.searchParams.delete("booking");
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  }, [state]);

  // Property, Source and Payment are applied client-side (Property/Source scope
  // KPIs + table + calendar together; payment status is a derived field).
  const visibleRows = useMemo(() => {
    if (state.kind !== "ready") return [];
    return state.rows.filter(
      (r) =>
        (propertyId === "" || r.propertyId === propertyId) &&
        matchesSource(r, source) &&
        (paymentStatus === "" || r.paymentStatus === paymentStatus),
    );
  }, [state, propertyId, source, paymentStatus]);

  // KPI summary RESPECTS the active Property/Source/Payment filters: recompute
  // from the visible rows so a single-property view shows only its numbers.
  const summary: BookingsSummary | null = useMemo(() => {
    if (state.kind !== "ready") return null;
    const base = state.summary;
    // With no client-side scoping, keep the server summary verbatim.
    if (propertyId === "" && source === "" && paymentStatus === "") return base;
    const today = todayIso();
    let confirmed = 0, held = 0, cancelled = 0, upcoming = 0;
    let revenue = 0n;
    for (const r of visibleRows) {
      if (r.status === "confirmed") {
        confirmed += 1;
        revenue += BigInt(r.amountMinor || "0");
        if (r.checkIn >= today) upcoming += 1;
      } else if (r.status === "held") held += 1;
      else if (r.status === "cancelled") cancelled += 1;
    }
    return {
      total: visibleRows.length,
      confirmed,
      held,
      cancelled,
      upcoming,
      confirmedRevenueMinor: revenue.toString(),
      currency: base.currency,
    };
  }, [state, visibleRows, propertyId, source, paymentStatus]);

  // Reset to page 1 whenever the filtered result set changes.
  useEffect(() => {
    setPage(1);
  }, [search, status, propertyId, source, paymentStatus, from, to]);

  // Pagination (table view) — 20 rows per page.
  const pageCount = Math.max(1, Math.ceil(visibleRows.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pagedRows = useMemo(
    () => visibleRows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [visibleRows, safePage],
  );

  // Calendar window + rows (exclude cancelled; they no longer hold inventory).
  const calDays = useMemo(() => buildDays(calStart, CALENDAR_DAYS), [calStart]);
  const calRows = useMemo(
    () => visibleRows.filter((r) => r.status !== "cancelled"),
    [visibleRows],
  );
  const calRangeLabel = useMemo(() => {
    const startIso = calDays[0]?.iso ?? calStart;
    const endIso = calDays[calDays.length - 1]?.iso ?? calStart;
    const fmt = (iso: string): string =>
      new Date(`${iso}T00:00:00`).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
      });
    return `${fmt(startIso)} – ${fmt(endIso)}`;
  }, [calDays, calStart]);

  const hasFilters =
    search !== "" ||
    status !== "" ||
    propertyId !== "" ||
    source !== "" ||
    paymentStatus !== "" ||
    from !== "" ||
    to !== "";

  function clearFilters(): void {
    setSearch("");
    setStatus("");
    setPropertyId("");
    setSource("");
    setPaymentStatus("");
    setFrom("");
    setTo("");
  }

  async function handleExpireHolds(): Promise<void> {
    setExpiring(true);
    try {
      const result = await apiExpireHolds();
      if (asError(result) === null) await load();
    } catch {
      /* non-fatal */
    } finally {
      setExpiring(false);
    }
  }

  return (
    <section aria-labelledby="bookings-heading" data-console-screen="bookings">
      <div className="bookings-head">
        <div>
          <p className="console-page-eyebrow">Reservations · Register</p>
          <h1 id="bookings-heading">Bookings</h1>
          <p className="console-lede">
            Every reservation across your direct and OTA channels in one
            register — filter, search, and open any booking for the full detail.
          </p>
        </div>
        {/* Secondary actions + Table ⇄ Calendar view toggle. */}
        <div className="bookings-head__actions">
          <button
            type="button"
            className="console-btn console-btn--ghost console-btn--sm"
            disabled={expiring}
            onClick={() => void handleExpireHolds()}
            title="Release expired holds back to inventory"
          >
            {expiring ? "Sweeping…" : "Expire holds"}
          </button>
        <div className="bookings-viewtoggle" role="group" aria-label="View">
          <button
            type="button"
            className={
              view === "table"
                ? "bookings-viewtoggle__btn bookings-viewtoggle__btn--active"
                : "bookings-viewtoggle__btn"
            }
            aria-pressed={view === "table"}
            onClick={() => setView("table")}
          >
            Table
          </button>
          <button
            type="button"
            className={
              view === "calendar"
                ? "bookings-viewtoggle__btn bookings-viewtoggle__btn--active"
                : "bookings-viewtoggle__btn"
            }
            aria-pressed={view === "calendar"}
            onClick={() => setView("calendar")}
          >
            Calendar
          </button>
        </div>
        </div>
      </div>

      {state.kind === "ready" && summary !== null ? <SummaryStrip summary={summary} /> : null}

      {/* Filter bar */}
      <div className="bookings-filters bookings-filters--panel" role="search">
        <div className="console-field bookings-filters__search">
          <label htmlFor="bk-search">Search</label>
          <input
            id="bk-search"
            type="search"
            placeholder="Guest name, email, or confirmation code"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="console-field">
          <label htmlFor="bk-property">Property</label>
          <select
            id="bk-property"
            value={propertyId}
            onChange={(e) => setPropertyId(e.target.value)}
          >
            <option value="">All properties</option>
            {properties.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
        <div className="console-field">
          <label htmlFor="bk-source">Source</label>
          <select
            id="bk-source"
            value={source}
            onChange={(e) => setSource(e.target.value)}
          >
            <option value="">All sources</option>
            {SOURCE_FILTERS.map((sf) => (
              <option key={sf.value} value={sf.value}>{sf.label}</option>
            ))}
          </select>
        </div>
        <div className="console-field">
          <label htmlFor="bk-status">Status</label>
          <select
            id="bk-status"
            value={status}
            onChange={(e) => setStatus(e.target.value as "" | ReservationStatus)}
          >
            <option value="">All statuses</option>
            <option value="confirmed">Confirmed</option>
            <option value="held">Held</option>
            <option value="cancelled">Cancelled</option>
            <option value="overbooked">Overbooked</option>
          </select>
        </div>
        <div className="console-field">
          <label htmlFor="bk-payment">Payment</label>
          <select
            id="bk-payment"
            value={paymentStatus}
            onChange={(e) => setPaymentStatus(e.target.value as "" | PaymentStatus)}
          >
            <option value="">All payments</option>
            {PAYMENT_STATUSES.map((p) => (
              <option key={p} value={p}>{PAYMENT_LABEL[p]}</option>
            ))}
          </select>
        </div>
        <div className="console-field">
          <label htmlFor="bk-from">Check-in from</label>
          <input id="bk-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div className="console-field">
          <label htmlFor="bk-to">Check-in to</label>
          <input id="bk-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        {hasFilters ? (
          <button
            type="button"
            className="console-btn console-btn--ghost bookings-filters__clear"
            onClick={clearFilters}
          >
            Clear
          </button>
        ) : null}
      </div>

      {flash !== null ? (
        <p className="rev-flash" role="status" aria-live="polite" style={{ margin: "0.5rem 0" }}>{flash}</p>
      ) : null}

      {state.kind === "loading" ? (
        <p className="console-loading" role="status" aria-live="polite">
          Loading bookings…
        </p>
      ) : null}

      {state.kind === "error" ? (
        <p className="console-empty" role="status">
          Bookings are unavailable right now. Try refreshing in a moment.
        </p>
      ) : null}

      {state.kind === "ready" && view === "calendar" ? (
        <section className="console-panel" aria-label="Bookings calendar">
          <div className="bookings-calnav">
            <h2 className="console-panel__title">Calendar</h2>
            <div className="bookings-calnav__controls">
              <span className="bookings-calnav__range">{calRangeLabel}</span>
              <button
                type="button"
                className="console-btn console-btn--ghost"
                onClick={() => setCalStart((s) => addDaysIso(s, -CALENDAR_DAYS))}
              >
                ‹ Prev
              </button>
              <button
                type="button"
                className="console-btn console-btn--ghost"
                onClick={() => setCalStart(todayIso())}
              >
                Today
              </button>
              <button
                type="button"
                className="console-btn console-btn--ghost"
                onClick={() => setCalStart((s) => addDaysIso(s, CALENDAR_DAYS))}
              >
                Next ›
              </button>
            </div>
          </div>
          <BookingsCalendar
            days={calDays}
            rows={calRows}
            onSelect={setSelected}
          />
        </section>
      ) : null}

      {state.kind === "ready" && view === "table" ? (
        <section className="console-panel" aria-label="Bookings">
          <h2 className="console-panel__title">
            {visibleRows.length} booking{visibleRows.length === 1 ? "" : "s"}
          </h2>
          {visibleRows.length > 0 ? (
            <table className="console-table bookings-table">
              <caption className="console-sr-only">All bookings</caption>
              <thead>
                <tr>
                  <th scope="col">Guest</th>
                  <th scope="col">Property / Unit</th>
                  <th scope="col">Guests</th>
                  <th scope="col">Stay</th>
                  <th scope="col">Source</th>
                  <th scope="col">Amount</th>
                  <th scope="col">Payment</th>
                  <th scope="col">Status</th>
                  <th scope="col">Sync</th>
                </tr>
              </thead>
              <tbody>
                {pagedRows.map((b) => (
                  <tr
                    key={b.id}
                    className={
                      selected !== null && selected.id === b.id
                        ? "bookings-row bookings-row--selected"
                        : "bookings-row"
                    }
                    {...(selected !== null && selected.id === b.id
                      ? { "aria-selected": true as const }
                      : {})}
                    onClick={() => setSelected(b)}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setSelected(b);
                      }
                    }}
                  >
                    <td>
                      <span className="bookings-guest">
                        {guestLabel(b.guestName, b.guestId)}
                      </span>
                      {b.confirmationCode !== null ? (
                        <span className="bookings-code">{b.confirmationCode}</span>
                      ) : null}
                    </td>
                    <td>
                      <span className="bookings-prop">{b.propertyName ?? "—"}</span>
                      {b.unitName !== null ? (
                        <span className="bookings-unit">{b.unitName}</span>
                      ) : null}
                    </td>
                    <td className="recon-num">{b.partySize ?? "—"}</td>
                    <td>
                      <span className="bookings-dates">
                        {fmtDate(b.checkIn)} → {fmtDate(b.checkOut)}
                      </span>
                      <span className="bookings-nights">
                        {b.nights} night{b.nights === 1 ? "" : "s"}
                      </span>
                    </td>
                    <td>
                      <span className="bookings-source">
                        <OtaLogo
                          code={b.source ?? "direct"}
                          name={displaySource(b)}
                          size={20}
                        />
                        {displaySource(b)}
                      </span>
                    </td>
                    <td className="recon-num">
                      {formatMoney(Number(b.amountMinor), b.currency)}
                    </td>
                    <td>
                      <span className={paymentPill(b.paymentStatus)}>
                        {PAYMENT_LABEL[b.paymentStatus]}
                      </span>
                    </td>
                    <td>
                      <span className={statusPill(b.status)}>
                        {STATUS_LABEL[b.status]}
                      </span>
                    </td>
                    <td>
                      {b.sourceType === "ota" ? (
                        <span className={syncPill(b.channelSyncStatus).cls}>
                          {syncPill(b.channelSyncStatus).label}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="console-empty">
              No bookings match these filters.
              {hasFilters ? " Try clearing them." : ""}
            </p>
          )}

          {visibleRows.length > PAGE_SIZE ? (
            <div className="bookings-pagination">
              <span className="bookings-pagination__info">
                Showing {(safePage - 1) * PAGE_SIZE + 1}–
                {Math.min(safePage * PAGE_SIZE, visibleRows.length)} of{" "}
                {visibleRows.length}
              </span>
              <div className="bookings-pagination__controls">
                <button
                  type="button"
                  className="console-btn console-btn--ghost"
                  disabled={safePage <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  ‹ Prev
                </button>
                <span className="bookings-pagination__page">
                  Page {safePage} of {pageCount}
                </span>
                <button
                  type="button"
                  className="console-btn console-btn--ghost"
                  disabled={safePage >= pageCount}
                  onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                >
                  Next ›
                </button>
              </div>
            </div>
          ) : null}
        </section>
      ) : null}

      {selected !== null ? (
        <DetailDrawer
          booking={selected}
          onClose={() => setSelected(null)}
          onChanged={(flash) => { setSelected(null); setFlash(flash); void load(); }}
        />
      ) : null}
    </section>
  );
}
